import { Day } from "@city-sage/core-client"
import { Calendar } from "lucide-react"
import { getDayColor } from "../../lib/constants/dayColors"
import { useDayStops } from "../../hooks/useDayStops"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@city-sage/aurum-avis-labs-components"

interface DaySelectionSectionProps {
  days: Day[] | undefined
  selectedDayId: string
  onDaySelect: (dayId: string) => void
  currentDayId: string
}

export function DaySelectionSection({ 
  days, 
  selectedDayId, 
  onDaySelect, 
  currentDayId 
}: DaySelectionSectionProps) {
  if (!days || days.length <= 1) {
    return null // Don't show if there's only one day or no days
  }

  const selectedDay = days.find(day => day.id === selectedDayId)

  return (
    <div className="bg-white px-6 py-6 border-b border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Assign to Day</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Choose which day this stop should be assigned to in your itinerary.
      </p>

      <Select value={selectedDayId} onValueChange={onDaySelect}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a day">
            {selectedDay && (
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full ${getDayColor(selectedDay.number - 1).bgClass} flex items-center justify-center text-white text-xs font-semibold`}
                >
                  {selectedDay.number}
                </div>
                <span>{selectedDay.title || `Day ${selectedDay.number}`}</span>
                {currentDayId === selectedDayId && (
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full ml-auto">
                    Current
                  </span>
                )}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {days.map((day, index) => (
            <DaySelectItem
              key={day.id}
              day={day}
              dayColor={getDayColor(day.number - 1)}
              isCurrentDay={currentDayId === day.id}
            />
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface DaySelectItemProps {
  day: Day
  dayColor: any
  isCurrentDay: boolean
}

function DaySelectItem({ day, dayColor, isCurrentDay }: DaySelectItemProps) {
  // Extract itineraryId from day
  const itineraryId = day.itineraryId
  
  // Use the hook to get stops count for this specific day
  const { data: dayStops } = useDayStops({
    itineraryId,
    dayId: day.id
  })

  return (
    <SelectItem value={day.id}>
      <div className="flex items-center gap-2 w-full">
        <div
          className={`w-6 h-6 rounded-full ${dayColor.bgClass} flex items-center justify-center text-white text-xs font-semibold`}
        >
          {day.number}
        </div>
        <div className="flex-1">
          <div className="font-medium">
            {day.title || `Day ${day.number}`}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <span>{dayStops?.length || 0} stops</span>
            {isCurrentDay && (
              <span className="bg-gray-100 px-1.5 py-0.5 rounded-full">
                Current
              </span>
            )}
          </div>
        </div>
      </div>
    </SelectItem>
  )
} 